import { Autocomplete, AutocompleteRenderInputParams, TextField } from '@mui/material'
import { pick } from 'lodash'
import { HTMLAttributes } from 'react'
import { FieldPath, FieldValues, useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  emptySelectOptionZod,
  NoOptionItem,
  optionToMenuItem,
  SelectContainer,
  SelectOption,
} from 'components/Select'
import { SelectAutocompleteProps } from './types'

export const SelectAutocomplete = <Schema extends FieldValues, Data>({
  control,
  disabled,
  disabledOptions,
  label,
  labelColor,
  name,
  onChange,
  optional,
  options,
  size,
  tooltip,
  width,
}: SelectAutocompleteProps<Schema, Data>): JSX.Element => {
  const { t } = useTranslation('components')

  const {
    field: { onChange: onChangeForm, value },
    fieldState: { error },
  } = useController<Schema>({
    control,
    name: name as FieldPath<Schema>,
  })

  return (
    <SelectContainer<Data>
      {...{
        disabled,
        error: Boolean(error),
        helperText: error?.message,
        label,
        name,
        tooltip,
        width,
      }}
    >
      <Autocomplete<SelectOption<Data>, false, true, false>
        {...(optional ? {} : { disableClearable: true })}
        autoHighlight
        disabled={disabled}
        getOptionLabel={(option: SelectOption<Data>): string => option.label ?? ''}
        id={name}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        onChange={(_, value: SelectOption<Data>): void => {
          let option = value
          if (optional && value === null) {
            option = pick(emptySelectOptionZod<Data>(), ['label', 'value'])
          }

          return onChange !== undefined ? onChange(option) : onChangeForm(option)
        }}
        options={options}
        renderInput={(params: AutocompleteRenderInputParams): JSX.Element => {
          const EndAdornment: JSX.Element | undefined = options.find(
            (option: SelectOption<Data>): boolean => option.value === value.value,
          )?.endAdornment

          return (
            <TextField
              {...params}
              data-cy={name}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {EndAdornment}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              label={label}
            />
          )
        }}
        renderOption={(props: HTMLAttributes<HTMLLIElement>, option: SelectOption<Data>) => {
          if (options.length === 0) {
            return <NoOptionItem t={t} />
          }

          const { endAdornment, label, logo, value } = option

          return optionToMenuItem<Data>({
            autocompleteOptionProps: props,
            disabledOptions,
            endAdornment,
            label,
            labelColor,
            logo,
            size,
            value,
          })
        }}
        value={value}
      />
    </SelectContainer>
  )
}
